import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageSquare, Filter, Image as ImgIcon, Circle, Clock, CheckCircle2 } from "lucide-react";

const STATI = [
  { key: "nuovo", label: "Nuovo", Icon: Circle, color: "text-secondary" },
  { key: "in_lavorazione", label: "In lavorazione", Icon: Clock, color: "text-amber-600" },
  { key: "risolto", label: "Risolto", Icon: CheckCircle2, color: "text-primary" },
];

export default function AdminFeedback() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/feedback", { params: filter ? { stato: filter } : {} });
      setItems(res.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  // Gating client-side (il vero gating è server-side su /api/admin/*)
  if (user && !user.is_admin) return <Navigate to="/app" replace />;

  const setStato = async (id, stato) => {
    try {
      await api.patch(`/admin/feedback/${id}`, { stato });
      toast.success(`Stato aggiornato → ${stato}`);
      setItems(prev => prev.map(x => x.id === id ? { ...x, stato } : x));
      if (selected?.id === id) setSelected({ ...selected, stato });
    } catch {
      toast.error("Errore aggiornamento");
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await api.get(`/admin/feedback/${id}`);
      setSelected(res.data);
    } catch {
      toast.error("Impossibile caricare il dettaglio");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-feedback-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl mb-1">Segnalazioni utenti</h1>
          <p className="text-sm text-muted-foreground">Feedback e bug reports inviati con il pulsante "Segnala un problema"</p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-9 rounded-full border bg-background px-4 text-sm"
            data-testid="admin-feedback-filter"
          >
            <option value="">Tutti gli stati</option>
            {STATI.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : items.length === 0 ? (
        <Card className="p-12 rounded-2xl text-center">
          <MessageSquare className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Nessuna segnalazione {filter ? `con stato "${filter}"` : "ancora"}.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map(fb => {
            const stato = STATI.find(s => s.key === fb.stato) || STATI[0];
            return (
              <Card key={fb.id} className="p-4 rounded-2xl border" data-testid={`feedback-item-${fb.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <stato.Icon className={`w-4 h-4 ${stato.color}`} />
                      <Badge variant="outline" className="rounded-full text-[10px]">{stato.label}</Badge>
                      {fb.regime && <Badge variant="secondary" className="rounded-full text-[10px]">{fb.regime}</Badge>}
                      {fb.has_screenshot && <Badge variant="outline" className="rounded-full text-[10px]"><ImgIcon className="w-3 h-3 mr-1" /> screenshot</Badge>}
                      <span className="text-xs text-muted-foreground">
                        {new Date(fb.created_at).toLocaleString("it-IT")}
                      </span>
                    </div>
                    <div className="text-sm font-medium">{fb.user_full_name || fb.user_email}</div>
                    <div className="text-xs text-muted-foreground mb-2">
                      <code>{fb.pagina || "—"}</code>
                    </div>
                    <p className="text-sm text-foreground/90 line-clamp-2">{fb.testo}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => openDetail(fb.id)} data-testid={`feedback-detail-${fb.id}`}>Dettaglio</Button>
                    <select
                      value={fb.stato}
                      onChange={(e) => setStato(fb.id, e.target.value)}
                      className="h-9 rounded-full border bg-background px-3 text-xs"
                      data-testid={`feedback-stato-${fb.id}`}
                    >
                      {STATI.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettaglio segnalazione</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-muted-foreground text-xs">Utente</div><div className="font-medium">{selected.user_full_name || "—"}</div></div>
                <div><div className="text-muted-foreground text-xs">Email</div><div className="font-medium">{selected.user_email}</div></div>
                <div><div className="text-muted-foreground text-xs">Regime</div><div>{selected.regime || "—"}</div></div>
                <div><div className="text-muted-foreground text-xs">Pagina</div><code className="text-xs">{selected.pagina || "—"}</code></div>
                <div className="col-span-2"><div className="text-muted-foreground text-xs">Browser</div><div className="text-xs">{selected.user_agent || "—"}</div></div>
                {selected.last_failed_api && (
                  <div className="col-span-2"><div className="text-muted-foreground text-xs">Ultimo errore API</div><code className="text-xs">{selected.last_failed_api}</code></div>
                )}
              </div>
              <div className="bg-muted/40 rounded-xl p-3">
                <div className="text-xs text-muted-foreground mb-1">Testo</div>
                <div className="whitespace-pre-wrap">{selected.testo}</div>
              </div>
              {selected.screenshot_b64 && (
                <div>
                  <div className="text-xs text-muted-foreground mb-2">Screenshot</div>
                  <img
                    src={selected.screenshot_b64.startsWith("data:") ? selected.screenshot_b64 : `data:image/png;base64,${selected.screenshot_b64}`}
                    alt="screenshot"
                    className="w-full rounded-lg border"
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
